<template>
  <UDashboardGroup>
    <UDashboardSearch />

    <UDashboardSidebar
      v-model:open="open"
      collapsible
      resizable
      class="bg-(--ui-bg-elevated)/75 border-r border-(--ui-border)"
      :ui="{ header: 'h-auto min-h-(--ui-header-height)', footer: 'lg:border-t lg:border-(--ui-border)' }"
    >
      <template #header="{ collapsed }">
        <div class="flex w-full items-center gap-2" :class="collapsed ? 'flex-col py-2' : 'flex-row'">
          <NuxtLink to="/" class="shrink-0">
            <div
              class="group rounded-xl bg-white dark:bg-zinc-800 flex items-center justify-center shadow-md shadow-black/10 hover:shadow-lg hover:shadow-teal-500/25 transition-all duration-200"
              :class="collapsed ? 'size-9' : 'size-14'"
            >
              <img
                src="/logo-icon.svg"
                alt="Logo"
                class="group-hover:hidden"
                :class="collapsed ? 'size-7' : 'size-12'"
              />
              <img
                src="/logo-text.svg"
                alt="GremiumHub"
                class="hidden group-hover:block h-auto"
                :class="collapsed ? 'w-7' : 'w-12'"
              />
            </div>
          </NuxtLink>
          <OrganizationSwitcher :collapsed="collapsed" class="min-w-0 flex-1" />
        </div>
      </template>

      <template #default="{ collapsed }">
        <UDashboardSearchButton :collapsed="collapsed" class="bg-transparent ring-(--ui-border)" />

        <UNavigationMenu :collapsed="collapsed" :items="links" orientation="vertical" />
      </template>

      <template #footer="{ collapsed }">
        <UserMenu :collapsed="collapsed" />
        <UButton
          v-if="showSapCopyButton"
          :loading="isDuplicatingSapForm"
          :disabled="isDuplicatingSapForm"
          icon="i-lucide-copy"
          size="xs"
          variant="soft"
          @click="duplicateSapS4HanaForTesting"
        >
          🖨
        </UButton>
      </template>
    </UDashboardSidebar>

    <slot />

    <NotificationsSlideover v-model="isNotificationsSlideoverOpen" />
  </UDashboardGroup>
</template>

<script setup lang="ts">
import { useNotificationStore } from '~~/stores/useNotificationStore'
import { useSeededSapS4HanaDuplicator } from '~/composables/testing/useSeededSapS4HanaDuplicator'
import { usePermissions } from '~/composables/usePermissions'

const { t: $t } = useI18n()
const { hasAnyRole } = usePermissions()

const links = computed(() => {
  const items = [
    {
      label: $t('documents.title'),
      icon: 'i-lucide-file-text',
      to: '/documents',
      exact: true
    },
    {
      label: $t('contact.title'),
      icon: 'i-lucide-mail',
      to: '/contact',
      exact: true
    },
    {
      label: $t('help.title'),
      icon: 'i-lucide-help-circle',
      to: '/help',
      exact: true
    },
    {
      label: $t('organization.title'),
      icon: 'i-lucide-building-2',
      to: '/organization',
      exact: true
    },
    {
      label: $t('signing.setup.navTitle'),
      icon: 'i-lucide-pen-tool',
      to: '/signing/setup',
      exact: true
    },
    {
      label: $t('settings.title'),
      icon: 'i-lucide-settings',
      to: '/settings',
      exact: true
    }
  ]

  if (hasAnyRole(['CHIEF_EXECUTIVE_OFFICER', 'IT_DEPARTMENT'])) {
    items.push({
      label: $t('administration.title'),
      icon: 'i-lucide-shield',
      to: '/administration',
      exact: true
    })
  }

  return items
})
const open = ref(false)

const isNotificationsSlideoverOpen = ref(false)
const notificationStore = useNotificationStore()
const { hasUnread } = storeToRefs(notificationStore)

const {
  showButton: showSapCopyButton,
  isDuplicating: isDuplicatingSapForm,
  duplicateSapS4HanaForTesting
} = useSeededSapS4HanaDuplicator()

let notificationRefreshInterval: ReturnType<typeof setInterval> | undefined

onMounted(async () => {
  await notificationStore.fetchUnreadCount()
  notificationRefreshInterval = notificationStore.startPeriodicRefresh()
})

onUnmounted(() => {
  if (notificationRefreshInterval) clearInterval(notificationRefreshInterval)
})

provide('notificationState', {
  isNotificationsSlideoverOpen,
  hasUnread
})
</script>
